import { useSearchParams } from 'react-router-dom';
import type { SignupRequest } from '../domain/account';
import { useRepositories } from '../hooks/useRepositories';
import SignUpForm from '../components/SignUpForm';

/**
 * Only a path on this site: `next` comes from the address bar. Resolved the
 * way the browser will resolve it, since `/\evil.example` and
 * `/<tab>/evil.example` start with one slash but load another site.
 */
function safeNext(next: string | null): string | null {
  if (!next?.startsWith('/')) return null;
  const here = window.location.origin;
  const resolved = new URL(next, here);
  return resolved.origin === here ? resolved.pathname + resolved.search + resolved.hash : null;
}

/**
 * `/sign-up` — make an account (019 US1). The server signs this browser in,
 * so success is a whole-page load of `next`, which Django serves signed in.
 * With an email or phone given and nowhere else to go, it lands on Emails
 * and phones, where that value waits to be verified.
 */
export default function SignUpPage({
  assign = (url: string) => window.location.assign(url),
}: {
  /** The full-page navigation; injected by tests. */
  assign?: (url: string) => void;
}) {
  const [params] = useSearchParams();
  const { session } = useRepositories();
  const next = safeNext(params.get('next'));

  const signUp = async (request: SignupRequest) => {
    await session.signUp(request);
    const gaveContact = !!(request.email || request.phone);
    assign(next ?? (gaveContact ? '/settings/account#emails-and-phones' : '/'));
  };

  return (
    <div className="mx-auto max-w-md">
      <section className="rounded-3xl bg-card p-8">
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Create your account</h1>
        <p className="mb-6 mt-1 text-sm text-muted-foreground">
          Main Quest is for the people you already trade with.
        </p>
        <SignUpForm onSubmit={signUp} next={next ?? '/'} />
        <p className="mt-6 text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <a
            href={session.signInUrl(next ?? '/')}
            className="font-semibold text-primary hover:underline"
          >
            Sign in
          </a>
        </p>
      </section>
    </div>
  );
}
